import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import { kycService, CreateKYCDocumentData, UpdateKYCDocumentData, KYCDocumentFilters } from '../services/kycService';
import { KYCDocument } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { useAppData } from '../context/AppDataContext';
import { FiUpload, FiDownload, FiX, FiCheckCircle, FiXCircle, FiClock, FiFile, FiEye } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import { formatDate } from '../utils/dateUtils';

const KYC: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { customers, refreshCustomers } = useAppData();
  const [documents, setDocuments] = useState<KYCDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState<KYCDocument | null>(null);
  const [filters, setFilters] = useState<KYCDocumentFilters>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<CreateKYCDocumentData>({
    customer_id: 0,
    document_type: '',
    status: 'pending',
    file: null as any
  });

  const hasLoadedRef = useRef(false);
  const filtersInitialized = useRef(false);

  // Refresh context data on page visit and load KYC documents list
  useEffect(() => {
    // Prevent duplicate calls from React StrictMode
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    
    const loadData = async () => {
      // Refresh customers in context to get latest from other users
      await refreshCustomers();
      // Always fetch fresh KYC documents list
      await loadDocuments();
      // Set after load completes to prevent filters useEffect from running on initial mount
      filtersInitialized.current = true;
    };
    loadData();
  }, []);

  // Reload documents when filters change (but not on initial mount)
  useEffect(() => {
    // Only run if filters have been initialized (after initial load completes)
    if (filtersInitialized.current) {
      loadDocuments();
    }
  }, [filters]);

  const loadDocuments = async () => {
    // Prevent duplicate concurrent calls
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const data = await kycService.getAll(filters);
      setDocuments(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load KYC documents');
    } finally {
      setLoading(false);
    }
  };


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!selectedFile && !editingDocument) {
      setError('Please select a file to upload');
      return;
    }

    try {
      if (editingDocument) {
        // Update existing document
        const updateData: UpdateKYCDocumentData = {
          document_type: formData.document_type,
          status: formData.status
        };
        await kycService.update(editingDocument.id, updateData);
      } else {
        // Upload new document
        await kycService.upload(formData);
      }
      
      setShowModal(false);
      setEditingDocument(null);
      setSelectedFile(null);
      setFormData({
        customer_id: 0,
        document_type: '',
        status: 'pending',
        file: null as any
      });
      // Refresh documents list and customers in context
      await Promise.all([loadDocuments(), refreshCustomers()]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save document');
    }
  };

  const handleEdit = (document: KYCDocument) => {
    setEditingDocument(document);
    setFormData({
      customer_id: document.customer_id,
      document_type: document.document_type,
      status: document.status,
      file: null as any
    });
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    try {
      await kycService.delete(id);
      // Refresh documents list and customers in context
      await Promise.all([loadDocuments(), refreshCustomers()]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete document');
    }
  };

  const handleDownload = async (kycDoc: KYCDocument) => {
    try {
      const blob = await kycService.download(kycDoc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = kycDoc.document_path.split('/').pop() || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download document');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; icon: any; label: string } } = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: FiClock, label: 'Pending' },
      'approved': { color: 'bg-green-100 text-green-800', icon: FiCheckCircle, label: 'Approved' },
      'rejected': { color: 'bg-red-100 text-red-800', icon: FiXCircle, label: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 ${config.color}`}>
        <Icon component={config.icon} className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const documentTypes = [
    'PAN Card',
    'Aadhaar Card',
    'GST Certificate',
    'Bank Statement',
    'Cancelled Cheque',
    'Company Registration',
    'Trade License',
    'Merged KYC Document',
    'Other'
  ];

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">KYC Document Management</h1>
          <button
            onClick={() => {
              setEditingDocument(null);
              setSelectedFile(null);
              setFormData({
                customer_id: 0,
                document_type: '',
                status: 'pending',
                file: null as any
              });
              setShowModal(true);
            }}
            className="bg-yellow-500 text-black-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-yellow-600 text-sm sm:text-base font-medium w-full sm:w-auto font-bold"
          >
            Upload Document
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Customer</label>
              <select
                value={filters.customer_id || ''}
                onChange={(e) => setFilters({ ...filters, customer_id: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Customers</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.party_name} {customer.customer_code && `(${customer.customer_code})`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as any || undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Document Type</label>
              <select
                value={filters.document_type || ''}
                onChange={(e) => setFilters({ ...filters, document_type: e.target.value || undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Types</option>
                {documentTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <button
              onClick={() => {
                setFilters({});
                loadDocuments();
              }}
              className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-300 text-sm font-medium"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-6 text-sm text-gray-600">Loading...</div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document Type
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Uploaded By
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Upload Date
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents.map((document) => (
                    <tr key={document.id} className="hover:bg-gray-50">
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm">
                        <div className="font-medium text-gray-900">{document.customer_name || '-'}</div>
                        {document.customer_code && (
                          <div className="text-xs text-gray-500">{document.customer_code}</div>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Icon component={FiFile} className="w-3 h-3" />
                          {document.document_type}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
                        {getStatusBadge(document.status)}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                        {document.uploaded_by_name || '-'}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden md:table-cell">
                        {formatDate(document.created_at)}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              try {
                                await kycService.view(document.id);
                              } catch (err: any) {
                                setError(err.response?.data?.message || 'Failed to view document');
                              }
                            }}
                            className="text-yellow-600 hover:text-yellow-700"
                            title="View"
                          >
                            <Icon component={FiEye} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownload(document)}
                            className="text-yellow-600 hover:text-yellow-700"
                            title="Download"
                          >
                            <Icon component={FiDownload} className="w-4 h-4" />
                          </button>
                          {hasPermission('kyc', 'edit') && (
                            <button
                              onClick={() => handleEdit(document)}
                              className="text-yellow-600 hover:text-yellow-700"
                              title="Edit"
                            >
                              Edit
                            </button>
                          )}
                          {hasPermission('kyc', 'delete') && (
                            <button
                              onClick={() => handleDelete(document.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Icon component={FiX} className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {documents.length === 0 && !loading && (
              <div className="text-center py-8 text-sm text-gray-500">No KYC documents found</div>
            )}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  {editingDocument ? 'Edit KYC Document' : 'Upload KYC Document'}
                </h3>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingDocument(null); }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Customer *</label>
                  <select
                    required
                    value={formData.customer_id || ''}
                    onChange={(e) => setFormData({ ...formData, customer_id: parseInt(e.target.value) })}
                    disabled={!!editingDocument}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Select Customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.party_name} {customer.customer_code && `(${customer.customer_code})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                  <select
                    required
                    value={formData.document_type}
                    onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  >
                    <option value="">Select Document Type</option>
                    {documentTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                {!editingDocument && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">File *</label>
                    <input
                      type="file"
                      required={!editingDocument}
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      className="block w-full text-xs sm:text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs sm:file:text-sm file:font-medium file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100"
                    />
                    <p className="mt-1 text-xs text-gray-500">Allowed: PDF, JPG, PNG, DOC, DOCX (Max 5MB)</p>
                    {selectedFile && (
                      <p className="mt-1 text-xs text-yellow-600">Selected: {selectedFile.name}</p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingDocument(null);
                      setSelectedFile(null);
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    {editingDocument ? 'Update' : 'Upload'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default KYC;

